import type { Issue } from '@/types/risk_score'
import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'

// 未対応（デフォルト状態）はタグを出さず、対応済・許容可のときだけ表示する。
function dismissChip (status: string | null | undefined): { label: string } | null {
  if (status === 'resolved') return { label: '対応済' }
  if (status === 'acceptable') return { label: '許容可' }
  return null
}

const SEVERITY_STYLE = {
  critical: {
    bar: 'bg-(--bp-critical)',
    badge: 'bg-(--bp-critical-pale) text-(--bp-critical)',
    label: 'CRITICAL',
    code: 'bg-[rgba(200,32,32,.07)] text-(--bp-critical) border-(--bp-critical-border)'
  },
  warning: {
    bar: 'bg-(--bp-warning)',
    badge: 'bg-(--bp-warning-pale) text-(--bp-warning)',
    label: 'WARNING',
    code: 'bg-[rgba(53,71,193,.08)] text-(--bp-accent) border-[rgba(53,71,193,.22)]'
  },
  info: {
    bar: 'bg-(--bp-info)',
    badge: 'bg-(--bp-info-pale) text-(--bp-info)',
    label: 'INFO',
    code: 'bg-[rgba(71,85,105,.07)] text-(--bp-info) border-(--bp-info-border)'
  }
} as const

export function RiskCard ({ issue, severity }: { issue: Issue, severity: 'critical' | 'warning' | 'info' }): React.ReactElement {
  const style = SEVERITY_STYLE[severity]
  const chip = dismissChip(issue.dismiss_status)
  const isDone = issue.dismiss_status != null

  return (
    <Card className={cn('flex overflow-hidden rounded-lg shadow-none p-0', isDone && 'opacity-48')}>
      <div className={cn('w-1 shrink-0', style.bar)} />
      <div className='flex-1 py-3.5 px-4'>
        <div className='flex items-center justify-between gap-2 mb-1.5 flex-wrap'>
          <div className='flex items-center gap-2 flex-wrap'>
            {(issue.code !== '') && (
              <span className={cn('text-[10px] font-bold px-1.75 py-0.5 rounded border font-mono tracking-[.02em]', style.code)}>
                [{issue.code_label ?? issue.code}]
              </span>
            )}
          </div>
          <div className='flex items-center gap-2 flex-wrap'>
            {(chip != null) && (
              <span className='ml-auto text-[10px] font-bold py-0.5 px-2.25 rounded-full whitespace-nowrap shrink-0 border bg-(--bp-ok-pale) text-(--bp-ok) border-(--bp-ok-border)'>
                {chip.label}
              </span>
            )}
            <span className={cn('text-[10px] font-bold tracking-[.06em] py-0.5 px-1.75 rounded', style.badge)}>
              {style.label}
            </span>
          </div>
        </div>
        <div className={cn('text-sm font-bold text-(--bp-ink)', (issue.remediation != null && issue.remediation !== '') ? 'mb-1.25' : '')}>{issue.message}</div>
        {(issue.remediation != null && issue.remediation !== '') && (
          <div className='text-xs text-(--bp-ink2) leading-relaxed'>{issue.remediation}</div>
        )}
        {(issue.entity != null && issue.entity !== '') && (
          <div className='mt-1.5 text-[11px] text-(--bp-ink3)'>対象: {issue.entity}</div>
        )}
      </div>
    </Card>
  )
}
